/**
 * wa-scrollbar — custom-styled scrollbar wrapper for any scrollable region.
 *
 * Wraps slotted content in a *native* scroll container (so keyboard, wheel,
 * touch, momentum and #anchor links all keep working) and overlays a
 * custom-drawn thumb + track that you style entirely with CSS.
 *
 * Usage:
 *   <script type="module" src="wa-scrollbar.js"></script>
 *
 *   Region:      <wa-scrollbar style="height: 400px"> …content… </wa-scrollbar>
 *   Page-level:  <wa-scrollbar style="height: 100vh">  …whole page… </wa-scrollbar>
 *
 * The host defaults to height: 100% — it fills a height-constrained parent
 * automatically. Scrolling happens once the host's resolved height is smaller
 * than its content; if neither the host nor an ancestor has a definite height,
 * the host just grows with its content and nothing scrolls.
 *
 * Attributes:
 *   axis   "both" (default) | "vertical" | "horizontal"
 *          Which direction(s) may scroll. A disabled axis is clipped
 *          (overflow: hidden) and shows no track.
 *   show   "scrolling" (default) | "always"
 *          "scrolling" — tracks fade in on scroll/drag and fade out shortly
 *                        after the user stops (they also stay while the
 *                        pointer is over a track).
 *          "always"    — tracks are shown whenever that axis overflows.
 *
 * Properties / methods:
 *   .viewport   the inner native scroll container (read-only) — read or set
 *               .scrollTop / .scrollLeft / .scrollTo() on it directly
 *   .axis       reflects the axis attribute (read/write)
 *   .show       reflects the show attribute (read/write)
 *   .update()   force a re-measure (call after programmatic content changes
 *               that ResizeObserver can't catch)
 *
 * Events:
 *   scroll      re-dispatched on the host whenever the viewport scrolls
 *
 * Accessibility:
 *   Scrolling is native, so keyboard scrolling works once the scroll region is
 *   focused. When the content overflows, that region is exposed to assistive
 *   tech as a focusable role="group". Name it by setting aria-label on the
 *   <wa-scrollbar> host — it is mirrored onto the region; with none it falls
 *   back to "Scrollable region". (aria-labelledby is not supported: IDs do not
 *   cross the shadow boundary — use aria-label.)
 *
 * Theming — CSS custom properties set on the host:
 *   --wa-sb-size             scrollbar thickness               (12px)
 *   --wa-sb-gap              inset from the content edge        (2px)
 *   --wa-sb-track-bg         track background                   (transparent)
 *   --wa-sb-track-radius     track corner radius                (8px)
 *   --wa-sb-thumb-bg         thumb fill                         (rgba(0,0,0,.5))
 *   --wa-sb-thumb-bg-hover   thumb fill on track/thumb hover     (rgba(0,0,0,.65))
 *   --wa-sb-thumb-bg-active  thumb fill while dragging           (rgba(0,0,0,.8))
 *   --wa-sb-thumb-radius     thumb corner radius                (8px)
 *   --wa-sb-thumb-min        minimum thumb length               (24px)
 *   --wa-sb-corner-bg        background of the bottom-right corner (transparent)
 *   --wa-sb-focus            keyboard focus ring colour          (oklch(55% .22 270))
 *
 *   For WCAG 1.4.11 the thumb should keep >=3:1 contrast against the content
 *   behind it. The defaults clear that over light backgrounds; on dark or
 *   busy content, override --wa-sb-thumb-bg* with a colour that does.
 *
 * Parts (for ::part() styling):
 *   viewport
 *   track, track-vertical, track-horizontal
 *   thumb, thumb-vertical, thumb-horizontal
 *   corner
 */

class WaScrollbar extends HTMLElement {
  /** @type {ShadowRoot} */ #shadow;
  /** @type {HTMLElement} */ #viewport;
  /** @type {HTMLElement} */ #trackV;
  /** @type {HTMLElement} */ #thumbV;
  /** @type {HTMLElement} */ #trackH;
  /** @type {HTMLElement} */ #thumbH;
  /** @type {HTMLElement} */ #corner;

  /** @type {ResizeObserver|null} */ #ro = null;
  /** Elements currently observed for size changes. @type {Set<Element>} */
  #observed = new Set();

  /** Active drag session, or null. */
  #drag = null;
  /** Pending requestAnimationFrame id (0 = none). */
  #raf = 0;
  /** Pending auto-hide timer id (0 = none). */
  #hideTimer = 0;
  /** True while the pointer is over a track (auto-hide mode stays open). */
  #hoveringTrack = false;

  // Geometry cached by #measure() so the per-scroll path touches no layout.
  // The heavy work (getComputedStyle, reading scrollHeight, sizing the thumbs)
  // runs only on resize / content / attribute changes — never while scrolling.
  #needMeasure = true;          // a full #measure() is due on the next frame
  #vActive = false;             // vertical axis currently scrollable
  #hActive = false;             // horizontal axis currently scrollable
  #vTravel = 0;                 // vertical thumb travel range, px
  #hTravel = 0;                 // horizontal thumb travel range, px
  #vMax = 0;                    // vertical max scrollTop, px
  #hMax = 0;                    // horizontal max scrollLeft, px
  #vPos = NaN;                  // last vertical thumb offset written
  #hPos = NaN;                  // last horizontal thumb offset written

  /** How long the tracks linger after scrolling stops, in `show="scrolling"`. */
  static #HIDE_DELAY = 900;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  constructor() {
    super();
    this.#shadow = this.attachShadow({ mode: 'open' });
    this.#shadow.innerHTML = WaScrollbar.#TEMPLATE;

    this.#viewport = this.#shadow.querySelector('.viewport');
    this.#trackV   = this.#shadow.querySelector('.track-v');
    this.#thumbV   = this.#shadow.querySelector('.thumb-v');
    this.#trackH   = this.#shadow.querySelector('.track-h');
    this.#thumbH   = this.#shadow.querySelector('.thumb-h');
    this.#corner   = this.#shadow.querySelector('.corner');

    // Shadow-internal elements live for the element's whole lifetime, so these
    // listeners are safe to bind once in the constructor.
    this.#viewport.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#thumbV.addEventListener('pointerdown', (e) => this.#beginDrag(e, 'v', this.#thumbV));
    this.#thumbH.addEventListener('pointerdown', (e) => this.#beginDrag(e, 'h', this.#thumbH));
    this.#trackV.addEventListener('pointerdown', (e) => this.#onTrackDown(e, 'v'));
    this.#trackH.addEventListener('pointerdown', (e) => this.#onTrackDown(e, 'h'));

    // Auto-hide mode: hovering a track pauses the hide timer so the user can
    // reach for the thumb without it fading away mid-grab.
    for (const track of [this.#trackV, this.#trackH]) {
      track.addEventListener('pointerenter', this.#onTrackEnter);
      track.addEventListener('pointerleave', this.#onTrackLeave);
    }

    const slot = this.#shadow.querySelector('slot');
    slot.addEventListener('slotchange', () => this.#observeContent());
  }

  static get observedAttributes() { return ['aria-label', 'axis', 'show']; }

  attributeChangedCallback(name, _old, val) {
    if (name === 'aria-label') this.#applyA11y();
    // axis changes the clipped direction; show changes auto-hide behaviour —
    // both need a re-measure (and `active` cleared if leaving "scrolling").
    if (name === 'axis' || name === 'show') {
      if (name === 'show' && val === 'always') this.removeAttribute('active');
      this.#schedule(true);
    }
  }

  connectedCallback() {
    this.#ro = new ResizeObserver(() => this.#schedule(true));
    this.#ro.observe(this.#viewport);
    this.#observeContent();
    this.#schedule(true);
  }

  disconnectedCallback() {
    this.#ro?.disconnect();
    this.#ro = null;
    this.#observed.clear();
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    clearTimeout(this.#hideTimer);
    this.#hideTimer = 0;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** The inner native scroll container. */
  get viewport() { return this.#viewport; }

  /** Which axes may scroll: "both" | "vertical" | "horizontal". */
  get axis()  { return this.getAttribute('axis') || 'both'; }
  set axis(v) {
    if (v == null) this.removeAttribute('axis');
    else this.setAttribute('axis', v);
  }

  /** When the tracks are shown: "scrolling" | "always". */
  get show()  { return this.getAttribute('show') === 'always' ? 'always' : 'scrolling'; }
  set show(v) {
    if (v == null) this.removeAttribute('show');
    else this.setAttribute('show', v);
  }

  /** Force a re-measure of content/viewport and reposition the thumbs. */
  update() { this.#needMeasure = true; this.#sync(); }

  // ── Measurement & sync ─────────────────────────────────────────────────────

  // Coalesce bursts of scroll / resize / mutation into one update per frame.
  // Pass full=true when geometry (not just scroll offset) may have changed.
  #schedule(full = false) {
    if (full) this.#needMeasure = true;
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#sync();
    });
  }

  // Per-frame update. The hot path (pure scrolling) skips #measure() entirely
  // and only reads scrollTop/scrollLeft + writes a compositor-only transform.
  #sync() {
    if (this.#needMeasure) {
      this.#needMeasure = false;
      this.#measure();
    }

    const vp = this.#viewport;

    if (this.#vActive) {
      const pos = this.#vMax > 0 ? vp.scrollTop / this.#vMax * this.#vTravel : 0;
      if (pos !== this.#vPos) {
        this.#vPos = pos;
        this.#thumbV.style.transform = `translate3d(0, ${pos}px, 0)`;
      }
    }
    if (this.#hActive) {
      const pos = this.#hMax > 0 ? vp.scrollLeft / this.#hMax * this.#hTravel : 0;
      if (pos !== this.#hPos) {
        this.#hPos = pos;
        this.#thumbH.style.transform = `translate3d(${pos}px, 0, 0)`;
      }
    }
  }

  // Heavy path: read the viewport, size the thumbs, show/hide tracks, and
  // cache the geometry #sync() needs. Runs only on resize / content / attribute
  // changes — never on a plain scroll — so per-frame style/layout cost is zero.
  #measure() {
    const vp = this.#viewport;
    const { scrollHeight, scrollWidth, clientHeight, clientWidth } = vp;

    // A disabled axis is clipped, so its content can still report overflow
    // even though it can't scroll — gate each axis on the `axis` attribute.
    const axis     = this.axis;
    const vEnabled = axis !== 'horizontal';
    const hEnabled = axis !== 'vertical';
    this.#vActive = vEnabled && scrollHeight - clientHeight > 1;
    this.#hActive = hEnabled && scrollWidth  - clientWidth  > 1;

    this.#trackV.hidden = !this.#vActive;
    this.#trackH.hidden = !this.#hActive;
    this.#corner.hidden = !(this.#vActive && this.#hActive);
    // Lets CSS shorten each track so they don't overlap in the corner.
    this.toggleAttribute('both', this.#vActive && this.#hActive);

    const minThumb = parseFloat(getComputedStyle(this.#thumbV).minHeight) || 24;

    if (this.#vActive) {
      const trackLen = this.#trackV.clientHeight;
      const thumbLen = Math.max(minThumb, Math.round(trackLen * clientHeight / scrollHeight));
      this.#vTravel = trackLen - thumbLen;
      this.#vMax    = scrollHeight - clientHeight;
      this.#vPos    = NaN; // force the next transform write
      this.#thumbV.style.height = `${thumbLen}px`;
    }
    if (this.#hActive) {
      const trackLen = this.#trackH.clientWidth;
      const thumbLen = Math.max(minThumb, Math.round(trackLen * clientWidth / scrollWidth));
      this.#hTravel = trackLen - thumbLen;
      this.#hMax    = scrollWidth - clientWidth;
      this.#hPos    = NaN;
      this.#thumbH.style.width = `${thumbLen}px`;
    }

    this.#applyA11y();
  }

  // Expose the scroll region to assistive tech. While the content overflows it
  // is a focusable role="group" (so keyboard users can scroll it and screen
  // readers announce it); otherwise it is left as a plain, non-focusable div.
  #applyA11y() {
    const vp = this.#viewport;
    if (this.#vActive || this.#hActive) {
      vp.tabIndex = 0;
      vp.setAttribute('role', 'group');
      vp.setAttribute('aria-label', this.getAttribute('aria-label') || 'Scrollable region');
    } else {
      vp.removeAttribute('tabindex');
      vp.removeAttribute('role');
      vp.removeAttribute('aria-label');
    }
  }

  // (Re)observe the slot's top-level elements so the thumbs update when
  // content grows or shrinks (images loading, dynamic insertions, …).
  #observeContent() {
    if (!this.#ro) return;
    const slot = this.#shadow.querySelector('slot');
    const els  = slot.assignedElements();

    for (const el of this.#observed) {
      if (!els.includes(el)) {
        this.#ro.unobserve(el);
        this.#observed.delete(el);
      }
    }
    for (const el of els) {
      if (!this.#observed.has(el)) {
        this.#ro.observe(el);
        this.#observed.add(el);
      }
    }
    this.#schedule(true);
  }

  // ── Events ─────────────────────────────────────────────────────────────────

  #onScroll = () => {
    this.#schedule();
    this.#wake();
    this.dispatchEvent(new Event('scroll'));
  };

  // ── Auto-hide (show="scrolling") ────────────────────────────────────────────

  // Reveal the tracks and (re)arm the idle timer. No-op in show="always".
  #wake() {
    if (this.show === 'always') return;
    if (!this.hasAttribute('active')) this.setAttribute('active', '');
    this.#scheduleHide();
  }

  // Hide the tracks once idle — unless a drag is in progress or a track is
  // hovered, in which case stay open (the next state change will reschedule).
  #scheduleHide() {
    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => {
      if (!this.#drag && !this.#hoveringTrack) this.removeAttribute('active');
    }, WaScrollbar.#HIDE_DELAY);
  }

  #onTrackEnter = () => {
    this.#hoveringTrack = true;
    clearTimeout(this.#hideTimer); // keep the tracks up while hovered
  };

  #onTrackLeave = () => {
    this.#hoveringTrack = false;
    this.#scheduleHide();
  };

  // Click on the track (not the thumb): jump so the thumb centres on the
  // pointer, then hand off to a drag so the user can keep scrubbing.
  #onTrackDown(e, axis) {
    if (e.button !== 0) return;
    const vp = this.#viewport;

    if (axis === 'v') {
      const rect     = this.#trackV.getBoundingClientRect();
      const thumbLen = this.#thumbV.offsetHeight;
      const travel   = this.#trackV.clientHeight - thumbLen;
      const target   = Math.max(0, Math.min(e.clientY - rect.top - thumbLen / 2, travel));
      const maxScroll = vp.scrollHeight - vp.clientHeight;
      vp.scrollTop = travel > 0 ? target / travel * maxScroll : 0;
    } else {
      const rect     = this.#trackH.getBoundingClientRect();
      const thumbLen = this.#thumbH.offsetWidth;
      const travel   = this.#trackH.clientWidth - thumbLen;
      const target   = Math.max(0, Math.min(e.clientX - rect.left - thumbLen / 2, travel));
      const maxScroll = vp.scrollWidth - vp.clientWidth;
      vp.scrollLeft = travel > 0 ? target / travel * maxScroll : 0;
    }

    this.#beginDrag(e, axis, axis === 'v' ? this.#trackV : this.#trackH);
  }

  // Start a thumb drag. `captureEl` receives pointer capture so the gesture
  // keeps tracking even if the pointer leaves the element.
  #beginDrag(e, axis, captureEl) {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation(); // when started on the thumb, don't also run the track handler

    const vp = this.#viewport;
    captureEl.setPointerCapture(e.pointerId);

    this.#drag = {
      axis,
      pointerId:   e.pointerId,
      captureEl,
      startClient: axis === 'v' ? e.clientY : e.clientX,
      startScroll: axis === 'v' ? vp.scrollTop : vp.scrollLeft,
    };
    this.setAttribute('dragging', '');
    this.#wake(); // keep the tracks visible for the whole drag (auto-hide mode)

    captureEl.addEventListener('pointermove',   this.#onDragMove);
    captureEl.addEventListener('pointerup',     this.#onDragEnd);
    captureEl.addEventListener('pointercancel', this.#onDragEnd);
  }

  #onDragMove = (e) => {
    const d = this.#drag;
    if (!d || e.pointerId !== d.pointerId) return;
    const vp = this.#viewport;

    if (d.axis === 'v') {
      const travel    = this.#trackV.clientHeight - this.#thumbV.offsetHeight;
      const maxScroll = vp.scrollHeight - vp.clientHeight;
      const delta     = e.clientY - d.startClient;
      vp.scrollTop = d.startScroll + (travel > 0 ? delta / travel * maxScroll : 0);
    } else {
      const travel    = this.#trackH.clientWidth - this.#thumbH.offsetWidth;
      const maxScroll = vp.scrollWidth - vp.clientWidth;
      const delta     = e.clientX - d.startClient;
      vp.scrollLeft = d.startScroll + (travel > 0 ? delta / travel * maxScroll : 0);
    }
  };

  #onDragEnd = (e) => {
    const d = this.#drag;
    if (!d || e.pointerId !== d.pointerId) return;
    d.captureEl.releasePointerCapture?.(d.pointerId);
    d.captureEl.removeEventListener('pointermove',   this.#onDragMove);
    d.captureEl.removeEventListener('pointerup',     this.#onDragEnd);
    d.captureEl.removeEventListener('pointercancel', this.#onDragEnd);
    this.#drag = null;
    this.removeAttribute('dragging');
    this.#scheduleHide(); // resume the auto-hide countdown
  };

  // ── Shadow DOM ─────────────────────────────────────────────────────────────

  static #TEMPLATE = `
<style>
  *, *::before, *::after { box-sizing: border-box; }

  :host {
    display: block;
    position: relative;
    overflow: hidden;
    /* Fills a height-constrained parent; falls back to content height (no
       scrolling) when the parent's height is indefinite. See the file header. */
    height: 100%;
  }

  :host([dragging]) {
    user-select: none;
    -webkit-user-select: none;
  }

  /* ── Native scroll container — real scrolling, native scrollbars hidden ── */
  .viewport {
    width: 100%;
    height: 100%;
    overflow: auto;
    outline: none;
    scrollbar-width: none;     /* Firefox */
    -ms-overflow-style: none;  /* legacy Edge */
  }
  .viewport::-webkit-scrollbar { width: 0; height: 0; } /* WebKit / Blink */

  /* Restrict scrolling to one axis — the other is clipped. */
  :host([axis="vertical"])   .viewport { overflow-x: hidden; }
  :host([axis="horizontal"]) .viewport { overflow-y: hidden; }

  .viewport:focus-visible {
    outline: 2px solid var(--wa-sb-focus, oklch(55% 0.22 270));
    outline-offset: -2px;
  }

  /* ── Tracks ── */
  .track {
    position: absolute;
    z-index: 1;
    background: var(--wa-sb-track-bg, transparent);
    border-radius: var(--wa-sb-track-radius, 8px);
    touch-action: none;
    opacity: 1;
    transition: opacity 200ms ease;
  }
  .track[hidden] { display: none; }

  /* Auto-hide (default / show="scrolling"): tracks stay hidden and
     non-interactive until JS marks the host [active] on scroll or drag. */
  :host(:not([show="always"])) .track {
    opacity: 0;
    pointer-events: none;
  }
  :host(:not([show="always"])[active]) .track {
    opacity: 1;
    pointer-events: auto;
  }

  .track-v {
    top:    var(--wa-sb-gap, 2px);
    bottom: var(--wa-sb-gap, 2px);
    right:  var(--wa-sb-gap, 2px);
    width:  var(--wa-sb-size, 12px);
  }
  .track-h {
    left:   var(--wa-sb-gap, 2px);
    right:  var(--wa-sb-gap, 2px);
    bottom: var(--wa-sb-gap, 2px);
    height: var(--wa-sb-size, 12px);
  }

  /* When both axes scroll, shorten each track to leave the corner free. */
  :host([both]) .track-v {
    bottom: calc(var(--wa-sb-size, 12px) + var(--wa-sb-gap, 2px) * 2);
  }
  :host([both]) .track-h {
    right: calc(var(--wa-sb-size, 12px) + var(--wa-sb-gap, 2px) * 2);
  }

  /* ── Thumbs ── */
  .thumb {
    position: absolute;
    background: var(--wa-sb-thumb-bg, rgba(0, 0, 0, 0.5));
    border-radius: var(--wa-sb-thumb-radius, 8px);
    transition: background 140ms ease;
    /* Keep the thumb on its own compositor layer so the per-scroll
       translate3d() update never triggers layout or paint. */
    will-change: transform;
  }
  .thumb-v {
    left: 0; right: 0; top: 0;
    min-height: var(--wa-sb-thumb-min, 24px);
  }
  .thumb-h {
    top: 0; bottom: 0; left: 0;
    min-width: var(--wa-sb-thumb-min, 24px);
  }

  .track:hover .thumb {
    background: var(--wa-sb-thumb-bg-hover, rgba(0, 0, 0, 0.65));
  }
  :host([dragging]) .thumb {
    background: var(--wa-sb-thumb-bg-active, rgba(0, 0, 0, 0.8));
  }

  /* ── Corner (only when both axes scroll) ── */
  .corner {
    position: absolute;
    right:  var(--wa-sb-gap, 2px);
    bottom: var(--wa-sb-gap, 2px);
    width:  var(--wa-sb-size, 12px);
    height: var(--wa-sb-size, 12px);
    background: var(--wa-sb-corner-bg, transparent);
  }
  .corner[hidden] { display: none; }

  @media (prefers-reduced-motion: reduce) {
    .track, .thumb { transition: none; }
  }
</style>

<div class="viewport" part="viewport"><slot></slot></div>

<!-- Tracks are a purely visual affordance — native scrolling carries the
     functionality, so they are hidden from assistive tech. -->
<div class="track track-v" part="track track-vertical" aria-hidden="true" hidden>
  <div class="thumb thumb-v" part="thumb thumb-vertical"></div>
</div>
<div class="track track-h" part="track track-horizontal" aria-hidden="true" hidden>
  <div class="thumb thumb-h" part="thumb thumb-horizontal"></div>
</div>
<div class="corner" part="corner" aria-hidden="true" hidden></div>
`;
}

if (!customElements.get('wa-scrollbar')) {
  customElements.define('wa-scrollbar', WaScrollbar);
}
